/**
 * Like/Favorite Hooks
 * 
 * Provides optimistic like/favorite functionality for documents.
 */

import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { getCandidateLikeUserIds } from "../utils/likeUserIds";

type UseToggleLikeParams = {
  doctype: string;
  name: string;
  initialLikedBy: string[];
};

/**
 * Hook for toggling like/favorite status on a document
 * 
 * Uses optimistic updates: UI updates immediately, then syncs with backend.
 * Reverts on error.
 * 
 * Note: Frappe's _liked_by field stores user emails/IDs. We compare with
 * currentUser.email to determine if the current user has liked the document.
 */
export function useToggleLike({ doctype, name, initialLikedBy }: UseToggleLikeParams) {
  const { data: currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  // Determine if current user has liked this document
  // Frappe stores user email/ID in _liked_by array
  const candidateUserIds = useMemo(() => {
    return getCandidateLikeUserIds(currentUser);
  }, [currentUser?.email, currentUser?.name]);

  const initiallyLiked = useMemo(
    () => candidateUserIds.length > 0 && initialLikedBy.some((u) => candidateUserIds.includes(u)),
    [initialLikedBy, candidateUserIds]
  );

  const [localLiked, setLocalLiked] = useState(initiallyLiked);

  const mutation = useMutation({
    mutationFn: async (nextLiked: boolean) => {
      return erp.docs.toggleLike({
        doctype,
        name,
        add: nextLiked,
      });
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ["doctype-list", doctype],
      });
      queryClient.invalidateQueries({
        queryKey: ["list", doctype],
      });
      // Also invalidate sidebar to refresh like count
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    },
    onError: () => {
      // Revert on error
      setLocalLiked((prev) => !prev);
    },
  });

  const toggle = () => {
    if (candidateUserIds.length === 0) return; // Can't like if not logged in

    const next = !localLiked;
    setLocalLiked(next); // Optimistic update
    mutation.mutate(next);
  };

  return {
    liked: localLiked,
    toggle,
    isLoading: mutation.isPending,
  };
}

